import { useMemo } from "react";
import { SearchableSelect } from "@/shared/components/common/SearchableSelect";

/** SearchableSelect has no empty entry, so "all institutes" uses a sentinel value. */
const ALL_VALUE = "__all__";

export interface InstituteFilterSelectProps {
  /** Selected institute id, "" for all institutes. */
  value: string;
  onChange: (instituteId: string) => void;
  /** Options as `{ value: id, label }`; undefined while they are not loaded. */
  options: { value: string; label: string }[] | undefined;
  isLoading: boolean;
  isError: boolean;
  labels: {
    /** Accessible name of the dropdown. */
    ariaLabel: string;
    /** Entry that clears the filter. */
    all: string;
    placeholder: string;
    loading: string;
    loadFailed: string;
    searchPlaceholder: string;
    viewAll: string;
  };
}

/**
 * Searchable institute dropdown for the users-API list headers (students, teachers),
 * shown next to the search box. Picking an option applies immediately; the "all" entry
 * clears it. Disabled while the options load or if loading them failed.
 */
export function InstituteFilterSelect({
  value,
  onChange,
  options,
  isLoading,
  isError,
  labels,
}: InstituteFilterSelectProps) {
  const allOptions = useMemo(
    () => [{ value: ALL_VALUE, label: labels.all }, ...(options ?? [])],
    [options, labels.all],
  );

  const placeholder = isLoading
    ? labels.loading
    : isError
      ? labels.loadFailed
      : labels.placeholder;

  return (
    <div className="w-[240px]">
      <SearchableSelect
        // No institute selected means "All institutes", which is the default shown.
        value={value || ALL_VALUE}
        onValueChange={(next) => onChange(next === ALL_VALUE ? "" : next)}
        options={allOptions}
        placeholder={placeholder}
        searchPlaceholder={labels.searchPlaceholder}
        viewAllLabel={labels.viewAll}
        disabled={isLoading || isError}
        aria-label={labels.ariaLabel}
        triggerClassName="gap-2 rounded-lg border-[#E5E7EB] [&>span:first-child]:min-w-0 [&>span:first-child]:truncate"
      />
    </div>
  );
}
